import { IconsConstants } from './../../common/icons-constants';
import { SharedConstants } from '../../common/shared-constants';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Injector,
  Input, OnInit,
  Output, ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatCalendar,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { EnumModel } from 'src/app/core/data/model';

import { DateFormatter, getObjectKeyByValue, subtractDays } from '../../helper';
import { DateFormatterPipe, EnumToArrayPipe } from '../../pipes';
import { BaseComponent } from '../base-component/base.component';
import { LanguageService } from 'src/app/core/helper';
import { ErrorValidationMessageComponent } from '../error-validation-message/error-validation-message.component';
import { IconSvgComponent } from '../icon/svg-icon.component';

@Component({
  selector: 'app-date-range',
  standalone: true,
  imports: [
    CommonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    MatIconModule,
    TranslateModule,
    MatMenuModule,
    EnumToArrayPipe,
    DateFormatterPipe,
    IconSvgComponent,
    ErrorValidationMessageComponent,
  ],
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy,
    },
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DateRangeComponent), multi: true },
  ],
})
export class DateRangeComponent extends BaseComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  selectedRangeLabel: string;
  selectedDateRange: DateRange<Date> = undefined;
  onChange: (date: any) => {};
  onTouched: any;
  isDisabled: boolean;
  form: FormGroup;
  inputValue: string;
  periodLabel: Date;
  formControl: AbstractControl;

  //inputs
  @Input() maxDate: Date = new Date();
  @Input() minDate: Date = new Date(new Date().setFullYear(2020, 4));
  @Input() isSingleDaySelect: boolean = false;
  @Input() label: string;

  // outputs
  @Output() dateRange: EventEmitter<DateRangeForm> = new EventEmitter<DateRangeForm>();

  // view childs
  @ViewChild('menu') menu: MatMenu;
  @ViewChild('calendar') calendar: MatCalendar<any>;

  // Constants
  SharedConstants = SharedConstants;
  IconsConstants = IconsConstants;
  // enums
  DateRanges = DateRanges;

  //getters
  get startDate(): AbstractControl {
    return this.form.get('startDate');
  }
  get endDate(): AbstractControl {
    return this.form.get('endDate');
  }
  get range(): AbstractControl {
    return this.form.get('range');
  }

  // setters
  set setInputValue(form: DateRangeForm) {
    if (form.range) {
      this.inputValue = form.range
        ? this.translationsList['ENUMS']['DATE_RANGES'][`${getObjectKeyByValue(DateRanges, form.range)}`]
        : null;
    } else if (form.start && !form.range) {
      if (this.isSingleDaySelect) {
        this.inputValue = `${
          form.start ? DateFormatter(this.injector, form.start, SharedConstants.SHORT_DATE_FORMAT) : ''
        }`;
      } else {
        this.inputValue = `${
          form.start ? DateFormatter(this.injector, form.start, SharedConstants.SHORT_DATE_FORMAT) : ''
        } - ${form.end ? DateFormatter(this.injector, form.end, SharedConstants.SHORT_DATE_FORMAT) : ''}`;
      }
    } else {
      this.inputValue = null;
    }
  }

  constructor(private dateAdapter: DateAdapter<Date>, injector: Injector) {
    super(['ENUMS'], injector);
    this.dateAdapter.setLocale(this.language);
    this.injector.get(LanguageService).currentLanguage$.subscribe((a) => {
      this.dateAdapter.setLocale(a.isoCode);
    });
  }
  //hooks
  ngOnInit(): void {
    this.initializeForm();
    this.getFormControl();
  }

  ngAfterViewInit(): void {
    this.setMatMenuPanelClass();
    this.periodLabel = this.calendar.activeDate;
    this.injector.get(ChangeDetectorRef).detectChanges();
  }

  // publics

  onSelectMonthOrYear(event: Date): void { 
    this.periodLabel = event; 
    this.calendar.activeDate = event;
  }

  onHeaderButtonClicked(mode: 'year' | 'month', changeMode: 'next' | 'prev'): void {
    if (mode == 'month') {
      if (changeMode == 'next') {
        this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1);
      }
      if (changeMode == 'prev') {
        this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1);
      }
    }
    this.periodLabel = this.calendar.activeDate;
  }

  onSelectDateRange(range: EnumModel): void {
    this.selectedRangeLabel = range.value;
    this.handleRangesCases(range);
    this.setFormValues(this.selectedDateRange.start, this.selectedDateRange.end, this.selectedRangeLabel);
    this.onChange({
      start: this.selectedDateRange.start,
      end: this.selectedDateRange.end,
      range: this.selectedRangeLabel,
    });
    this.dateRange.emit({
      start: this.selectedDateRange.start,
      end: new Date(new Date(this.selectedDateRange.end).setHours(23, 59, 59, 999)),
      range: this.selectedRangeLabel,
    });
    this.goToViewAfterSelectDate();
  }

  onChangeSelectedDateRange(date: Date): void {
    if (this.checkIfStartDateIsSelectedAndSetEndDate(date)) {
      if (this.isSingleDaySelect) {
        this.selectedDateRange = new DateRange(this.selectedDateRange.start, this.selectedDateRange.start);
      } else {
        this.selectedDateRange = new DateRange(this.selectedDateRange.start, date);
      }
    } else {
      if (this.isSingleDaySelect) {
        this.selectedDateRange = new DateRange(date, date);
      } else {
        this.selectedDateRange = new DateRange(date, null);
      }
    }
    this.selectedRangeLabel = null;
    this.setFormValues(this.selectedDateRange.start, this.selectedDateRange.end, this.selectedRangeLabel);
    this.onChange({
      start: this.selectedDateRange.start,
      end: new Date(new Date(this.selectedDateRange.end).setHours(23, 59, 59, 999)),
      range: this.selectedRangeLabel,
    });
    this.dateRange.emit({
      start: this.selectedDateRange.start,
      end: new Date(new Date(this.selectedDateRange.end).setHours(23, 59, 59, 999)),
      range: this.selectedRangeLabel,
    });
  }

  onSelectDate(menu: MatMenuTrigger): void {
    if (this.form.disabled) {
      menu.closeMenu();
    }
  }

  onCloseMenu(): void {
    this.onTouched();
  }

  // control value accessor interface methods
  writeValue(obj: DateRangeForm): void {
    this.setFormValues(obj?.start, this.isSingleDaySelect ? obj?.start : obj?.end, obj?.range);
    this.selectedDateRange = new DateRange(
      new Date(obj?.start),
      this.isSingleDaySelect ? new Date(obj?.start) : new Date(obj?.end)
    );
    this.selectedRangeLabel = obj?.range;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  // privates
  private setMatMenuPanelClass(): void {
    this.menu.panelClass = 'datePanel';
  }

  private initializeForm(): void {
    this.form = new FormGroup({
      startDate: new FormControl(),
      endDate: new FormControl(),
      range: new FormControl(),
    });
  }

  private setFormValues(start: Date, end: Date, range: string): void {
    this.startDate.setValue(start);
    this.endDate.setValue(end);
    this.range.setValue(range);
    this.setInputValue = { start, end, range };
  }

  private goToViewAfterSelectDate(): void {
    if (this.selectedDateRange.end) {
      this.calendar._goToDateInView(this.selectedDateRange.end, 'month');
    }
  }

  private handleRangesCases(range: EnumModel): void {
    switch (range.value) {
      case DateRanges.LAST_WEEK: {
        this.selectedDateRange = new DateRange(this.setStartDate(subtractDays(7, new Date())), this.setEndDate(new Date()));
        break;
      }
      case DateRanges.LAST_MONTH: {
        this.selectedDateRange = new DateRange(this.setStartDate(subtractDays(30, new Date())), this.setEndDate(new Date()));
        break;
      }
      case DateRanges.LAST_3_MONTHS: {
        this.selectedDateRange = new DateRange(this.setStartDate(subtractDays(30 * 3, new Date())), this.setEndDate(new Date()));
        break;
      }
      case DateRanges.LAST_6_MONTHS: {
        this.selectedDateRange = new DateRange(this.setStartDate(subtractDays(30 * 6, new Date())), this.setEndDate(new Date()));
        break;
      }
      case DateRanges.LAST_YEAR: {
        this.selectedDateRange = new DateRange(this.setStartDate(subtractDays(365, new Date())), this.setEndDate(new Date()));
        break;
      }
      case DateRanges.YESTERDAY: {
        this.selectedDateRange = new DateRange(this.setStartDate(subtractDays(1, new Date())), this.setEndDate(subtractDays(1, new Date())));
        break;
      }
      case DateRanges.TODAY: {
        this.selectedDateRange = new DateRange(this.setStartDate(new Date()), this.setEndDate(new Date()));
        break;
      }
    }
  }

  private checkIfStartDateIsSelectedAndSetEndDate(date: Date): boolean {
    return (
      this.selectedDateRange &&
      this.selectedDateRange.start &&
      date >= this.selectedDateRange.start &&
      !this.selectedDateRange.end
    );
  }
  private getFormControl(): void {
    const ngControl: NgControl = this.injector.get(NgControl);
    const formDirective: FormGroupDirective = this.injector.get(FormGroupDirective);
    const controlName = ngControl.name;
    this.formControl = formDirective.form.controls[controlName];
  }

  private setStartDate(date: Date): Date {
    return new Date(new Date(date).setHours(0, 0, 0, 0));
  }

  private setEndDate(date: Date): Date {
    return new Date(new Date(date).setHours(23, 59, 59, 999));
  }
}

export enum DateRanges {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST_WEEK = 'last-week',
  LAST_MONTH = 'last-month',
  LAST_3_MONTHS = 'last-3-months',
  LAST_6_MONTHS = 'last-6-months',
  LAST_YEAR = 'last-year',
}

export interface DateRangeForm {
  start: Date | undefined | null;
  end: Date | undefined | null;
  range?: string;
}
